import React, { useEffect, useRef } from 'react';

import { Link } from 'react-router-dom';

import {
  CheckCircleFilled,
  CloseCircleFilled,
  DashboardOutlined,
  SyncOutlined,
  TagOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { schemas } from '@recurrency/core-api-schema';
import { AsyncJobDTO, AsyncJobName, AsyncJobStatus } from '@recurrency/core-api-schema/dist/asyncJobs/common';
import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import { Tooltip } from 'antd';
import { ColumnType } from 'antd/lib/table';
import moment, { Moment } from 'moment';
import { colors } from 'theme/colors';
import { useDebounce } from 'use-debounce/lib';

import { AsyncMultiSelect } from 'components/AsyncSelect/AsyncMultiSelect';
import { MultiSelectOption, UseAsyncMultiSelectProps } from 'components/AsyncSelect/types';
import { useTenantUsersSelectProps } from 'components/AsyncSelect/useAsyncSelectProps';
import { AsyncTable } from 'components/AsyncTable';
import { useCoreApiTableProps } from 'components/AsyncTable/useAsyncTableProps';
import { ActionButton } from 'components/Button/ActionButton';
import { Container } from 'components/Container';
import { DateRangePicker } from 'components/DatePicker';
import { FilterBarBox } from 'components/FilterBarBox';
import { FlexSpacer } from 'components/FlexSpacer';
import { ButtonLink } from 'components/Links';
import { PageHeader } from 'components/PageHeader';
import { ResultCount } from 'components/ResultCount';
import { SearchInput } from 'components/SearchInput';
import { Tabs } from 'components/Tabs';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { truthy } from 'utils/boolean';
import { capitalizeFirst, formatDuration, formatName } from 'utils/formatting';
import { objMapValues } from 'utils/object';
import { routes, useHashState } from 'utils/routes';
import { asKeyOf, sortableDateColumn, sortableNumberColumn } from 'utils/tables';
import { getTenantSetting } from 'utils/tenantSettings';

import { ExportJobsPageHashState, ExportJobsTab } from 'types/hash-state';

import { AutomatedExportsTab } from './AutomatedExports/AutomatedExportsTab';
import { StepStatusProgressBar } from './StepStatusProgressBar';
import { formatJobName, iconStyle } from './utils';

const statusOptions: MultiSelectOption[] = [
  { label: 'All', value: '' },
  { label: 'Running', value: AsyncJobStatus.Running },
  { label: 'Success', value: AsyncJobStatus.Success },
  { label: 'Failed', value: AsyncJobStatus.Failed },
];

export const ExportJobsPage = () => {
  const [hashState, updateHashState] = useHashState<ExportJobsPageHashState>();
  const isAutomatedMinMaxEnabled = Boolean(getTenantSetting(TenantSettingKey.FeatureAutomatedMinMax));
  const activeTab = hashState.tab ?? ExportJobsTab.ALL;

  return (
    <Container>
      <PageHeader
        title="Export Jobs Status"
        subtitle="This page provides a detailed log of individual export jobs
       from Recurrency to your ERP, including timestamps and success/failure statuses."
      />
      {/* without automated min/max there is only one view, so the page stays as it was: no tab strip */}
      {!isAutomatedMinMaxEnabled ? (
        <ExportJobsTable />
      ) : (
        <Tabs
          activeTab={activeTab}
          // the two tabs share hash state, so drop filters that only make sense on the job list
          onChange={(tab) =>
            updateHashState({ tab: tab as ExportJobsTab, page: 1, userIds: undefined, runId: undefined })
          }
          // antd keeps a visited tab pane mounted, so only the active tab renders. Otherwise both would
          // keep polling on their 30s interval and refetch on every shared hash state change.
          tabs={[
            {
              tabKey: ExportJobsTab.ALL,
              header: 'Export Jobs',
              content: activeTab === ExportJobsTab.ALL ? <ExportJobsTable /> : <></>,
            },
            {
              tabKey: ExportJobsTab.AUTOMATED,
              header: 'Automated Exports',
              content: activeTab === ExportJobsTab.AUTOMATED ? <AutomatedExportsTab /> : <></>,
            },
          ]}
        />
      )}
    </Container>
  );
};

/** The full export job list, covering every job type. */
const ExportJobsTable = () => {
  const { activeUser } = useGlobalApp();
  const [hashState, updateHashState] = useHashState<ExportJobsPageHashState>();
  const tenantsUsersSelectProps = useTenantUsersSelectProps();
  const [debouncedSearchQuery] = useDebounce(hashState.searchQuery, 500);

  // automated runs live on their own tab, so they are neither listed nor offered as a filter here.
  // With the setting off there is no other tab, so they stay visible rather than becoming unreachable.
  const isAutomatedMinMaxEnabled = Boolean(getTenantSetting(TenantSettingKey.FeatureAutomatedMinMax));
  const selectableJobNames = Object.values(AsyncJobName).filter(
    (name) => !isAutomatedMinMaxEnabled || name !== AsyncJobName.AutomatedMinMaxUpdate,
  );
  const jobNameOptions: MultiSelectOption[] = selectableJobNames.map((name) => ({
    label: formatJobName(name),
    value: name,
  }));

  const tableProps = useCoreApiTableProps({
    schema: schemas.asyncJobs.getAsyncJobs,
    queryParams: {
      searchQuery: debouncedSearchQuery,
      filter: {
        status: hashState.status,
        userIds: hashState.userIds,
        // the API filter is an allow-list, so excluding automated runs means naming every other type
        jobNames: hashState.jobNames?.length ? hashState.jobNames : selectableJobNames,
        dateFrom: hashState.dateRange?.[0],
        dateTo: hashState.dateRange?.[1],
      },
      limit: 20,
      offset: ((hashState.page || 1) - 1) * 20,
    },
  });

  // useRef needed to reload latest params on interval
  const tablePropsRef = useRef<typeof tableProps>();
  tablePropsRef.current = tableProps;

  const updateFilter = (update: Partial<ExportJobsPageHashState>) => {
    updateHashState({ ...update, page: 1 });
  };

  useEffect(() => {
    // on initial page load, filter jobs to active user
    if (!hashState.userIds && !activeUser.isRecurrencyInternalUser) {
      updateHashState({ userIds: [activeUser.id] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // auto refresh page every 30s while user is on the page, to show latest progress
  useEffect(() => {
    const refreshIntervalId = setInterval(() => tablePropsRef.current?.reload(), 30_000);
    return () => {
      clearInterval(refreshIntervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderJobStatus = (status: AsyncJobStatus) => {
    switch (status) {
      case AsyncJobStatus.Success:
        return <CheckCircleFilled style={{ ...iconStyle, color: colors.success[500] }} />;
      case AsyncJobStatus.Running:
        return <SyncOutlined spin style={{ ...iconStyle, color: colors.info[500] }} />;
      case AsyncJobStatus.Failed:
        return <CloseCircleFilled style={{ ...iconStyle, color: colors.danger[500] }} />;
      default:
        return <></>;
    }
  };

  const columns: (ColumnType<AsyncJobDTO> | null)[] = [
    {
      title: 'Status',
      dataIndex: asKeyOf<AsyncJobDTO>('status'),
      width: 80,
      align: 'center',
      render: (status: AsyncJobStatus) => <Tooltip title={capitalizeFirst(status)}>{renderJobStatus(status)}</Tooltip>,
    },
    {
      title: 'Export Type',
      dataIndex: asKeyOf<AsyncJobDTO>('name'),
      render: (name: AsyncJobName, record) => (
        <Link to={routes.monitoring.exportJobDetails(record.id)}>{formatJobName(name)}</Link>
      ),
    },
    {
      title: 'User',
      dataIndex: asKeyOf<AsyncJobDTO>('user'),
      render: (user: AsyncJobDTO['user']) => formatName(user.firstName, user.lastName),
    },
    sortableNumberColumn({
      title: 'Record Count',
      dataIndex: asKeyOf<AsyncJobDTO>('recordCount'),
      sorter: true,
    }),
    sortableDateColumn({
      title: 'Started',
      dataIndex: asKeyOf<AsyncJobDTO>('createdAt'),
      sorter: true,
      withTimestamp: true,
      inLocalTime: true,
      defaultSortOrder: 'descend',
    }),
    sortableNumberColumn({
      title: 'Duration',
      dataIndex: asKeyOf<AsyncJobDTO>('durationMs'),
      render: (durationMs: number) => formatDuration(durationMs),
      sorter: true,
    }),
    {
      title: 'Progress',
      dataIndex: asKeyOf<AsyncJobDTO>('stepStatusCounts'),
      width: 200,
      render: (stepStatusCounts: AsyncJobDTO['stepStatusCounts']) =>
        stepStatusCounts && <StepStatusProgressBar statusCounts={stepStatusCounts} width={200} />,
    },
    {
      dataIndex: asKeyOf<AsyncJobDTO>('id'),
      render: (id) => ButtonLink(routes.monitoring.exportJobDetails(id)),
    },
  ];

  return (
    <>
      <FilterBarBox dividerLine>
        <AsyncMultiSelect
          label="Status"
          mode="single"
          icon={<DashboardOutlined />}
          selectProps={{ options: statusOptions }}
          selectedValues={[hashState.status || statusOptions[0].value]}
          onSelectedValuesChange={(values) => updateFilter({ status: (values[0] || undefined) as AsyncJobStatus })}
        />
        <AsyncMultiSelect
          label="Export Type"
          icon={<TagOutlined />}
          selectProps={{ options: jobNameOptions }}
          selectedValues={hashState.jobNames ?? []}
          onSelectedValuesChange={(values) => updateFilter({ jobNames: values })}
        />
        <AsyncMultiSelect
          label="User"
          icon={<UserOutlined />}
          queryPlaceholder="Search users"
          selectProps={tenantsUsersSelectProps as UseAsyncMultiSelectProps}
          selectedValues={hashState.userIds ? hashState.userIds : []}
          onSelectedValuesChange={(values) => updateFilter({ userIds: values })}
        />

        <DateRangePicker
          value={
            hashState.dateRange
              ? (hashState.dateRange.map((v) => moment(v)) as [Moment, Moment])
              : [moment().subtract(1, 'month'), moment()]
          }
          onChange={(values) =>
            updateFilter({ dateRange: values ? values.map((m) => m!.utc().format('YYYY-MM-DD')) : undefined })
          }
          format="MM/DD/YYYY"
        />
        {Object.keys(hashState).filter((key) => key !== asKeyOf<ExportJobsPageHashState>('tab')).length > 0 ? (
          <ActionButton
            // keep `tab` so clearing filters doesn't also switch the user back to the first tab
            onClick={() => updateFilter({ ...objMapValues(hashState as Any, () => undefined), tab: hashState.tab })}
            label="Clear All"
          />
        ) : null}
        <FlexSpacer />
        <SearchInput
          placeholder="Search by ID"
          query={hashState.searchQuery || ''}
          onQueryChange={(query) => updateFilter({ searchQuery: query })}
        />
        <ResultCount count={tableProps.totalCount} />
      </FilterBarBox>
      <AsyncTable
        tableProps={{
          ...tableProps,
          page: hashState.page || 1,
          setPage: (pageNum) => updateHashState({ page: pageNum }),
        }}
        columns={columns.filter(truthy)}
      />
    </>
  );
};
